import React, { useState, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "react-query";
import PropTypes from "prop-types";
import { recruitingAPI } from "../services/recruitingApi";
import { StatusBadge, ArchiveModal, SIZE_STYLES } from "./StatusControl";
import {
  CANDIDATE_STATUS_CONFIG as STATUS_CONFIG,
  CANDIDATE_ARCHIVE_REASONS as ARCHIVE_REASONS,
  CANDIDATE_STATUS_ORDER as STATUS_ORDER,
  CANDIDATE_VALID_TRANSITIONS as VALID_TRANSITIONS,
  getCandidateDisabledReason as getDisabledReason,
  CANDIDATE_CONFIG,
} from "./StatusControl/configs";

/**
 * CandidateStatusControl Component
 *
 * ADO Feature #4936: Candidate Status Workflow
 * Displays candidate status with badge and allows status transitions
 */
const CandidateStatusControl = ({
  candidate,
  onStatusChange,
  showTransitions = true,
  size = "medium",
  disabled = false,
}) => {
  const queryClient = useQueryClient();
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [transitionNote, setTransitionNote] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [gdprError, setGdprError] = useState(null);

  const currentStatus = candidate?.status || "preparation";
  const statusConfig =
    STATUS_CONFIG[currentStatus] || STATUS_CONFIG.preparation;

  // Fetch allowed transitions
  const { data: transitionsData } = useQuery(
    ["candidateTransitions", candidate?.id],
    () => recruitingAPI.getAllowedTransitions(candidate.id),
    {
      enabled: !!candidate?.id && showTransitions,
      staleTime: 30 * 1000,
    },
  );

  const allowedTransitions = transitionsData?.data?.allowed_transitions || [];

  // Update status mutation
  const updateStatusMutation = useMutation(
    (statusData) =>
      recruitingAPI.updateCandidateStatus(candidate.id, statusData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["candidate", String(candidate.id)]);
        queryClient.invalidateQueries(["candidates"]);
        queryClient.invalidateQueries(["candidateTransitions", candidate.id]);
        if (onStatusChange) {
          onStatusChange(data.data);
        }
      },
    },
  );

  // Archive mutation
  const archiveMutation = useMutation(
    (archiveData) => recruitingAPI.archiveCandidate(candidate.id, archiveData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["candidate", String(candidate.id)]);
        queryClient.invalidateQueries(["candidates"]);
        queryClient.invalidateQueries(["candidateTransitions", candidate.id]);
        setShowArchiveModal(false);
        if (onStatusChange) {
          onStatusChange(data.data);
        }
      },
    },
  );

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "archived") {
      setShowArchiveModal(true);
      return;
    }

    // Clear any previous GDPR error
    setGdprError(null);

    try {
      const statusData = {
        status: newStatus,
        note: transitionNote,
      };
      if (newStatus === "to_come_back" && followUpDate) {
        statusData.follow_up_date = followUpDate;
      }
      await updateStatusMutation.mutateAsync(statusData);
      setTransitionNote("");
      setFollowUpDate("");
    } catch (error) {
      console.error("Status change failed:", error);
      // Check if this is a GDPR consent required error
      const errorResponse = error?.response?.data;
      if (errorResponse?.code === "GDPR_CONSENT_REQUIRED") {
        setGdprError({
          message:
            errorResponse.message ||
            "GDPR consent is required before moving to Ready for Sale status.",
          candidateId: candidate.id,
        });
      }
    }
  };

  const handleArchive = async (archiveData) => {
    try {
      await archiveMutation.mutateAsync(archiveData);
    } catch (error) {
      console.error("Archive failed:", error);
    }
  };

  const badgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "9999px",
    fontWeight: "500",
    backgroundColor: statusConfig.bgColor,
    color: statusConfig.color,
    border: `1px solid ${statusConfig.color}`,
    ...SIZE_STYLES[size],
  };

  const isLoading = updateStatusMutation.isLoading || archiveMutation.isLoading;

  return (
    <div className="candidate-status-control">
      {/* Status Badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <span style={badgeStyle}>{statusConfig.label}</span>

        {currentStatus === "archived" && candidate?.archive_reason && (
          <span style={{ fontSize: "12px", color: "#6b7280" }}>
            (
            {ARCHIVE_REASONS[candidate.archive_reason]?.label ||
              candidate.archive_reason}
            )
          </span>
        )}

        {currentStatus === "hired" && candidate?.open_for_more_projects && (
          <span
            style={{
              ...badgeStyle,
              backgroundColor: "#dbeafe",
              color: "#2563eb",
              border: "1px solid #2563eb",
            }}
          >
            Open for Projects
          </span>
        )}

        {currentStatus === "archived" && candidate?.duplicate_of_id && (
          <a
            href={`/recruiting/candidates/${candidate.duplicate_of_id}`}
            style={{
              fontSize: "12px",
              color: "#3b82f6",
              textDecoration: "underline",
            }}
          >
            View main profile
          </a>
        )}
      </div>

      {/* Status Transitions */}
      {showTransitions && allowedTransitions.length > 0 && !disabled && (
        <div style={{ marginTop: "12px" }}>
          <div
            style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}
          >
            Change status to:
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {allowedTransitions.map((transition) => {
              const config = STATUS_CONFIG[transition.value] || {};
              return (
                <button
                  key={transition.value}
                  onClick={() => handleStatusChange(transition.value)}
                  disabled={isLoading}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    borderRadius: "6px",
                    border: `1px solid ${config.color || "#d1d5db"}`,
                    backgroundColor: "white",
                    color: config.color || "#374151",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.6 : 1,
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor =
                      config.bgColor || "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "white";
                  }}
                >
                  {transition.label}
                </button>
              );
            })}
          </div>

          {/* Follow-up date picker for to_come_back transition (US5) */}
          {allowedTransitions.some((t) => t.value === "to_come_back") && (
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <label
                htmlFor="follow-up-date"
                style={{ fontSize: "12px", color: "#6b7280" }}
              >
                Follow-up date:
              </label>
              <input
                id="follow-up-date"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                style={{
                  padding: "4px 8px",
                  fontSize: "12px",
                  borderRadius: "4px",
                  border: "1px solid #d1d5db",
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* GDPR Consent Required Modal */}
      {gdprError && (
        <GdprConsentModal
          error={gdprError}
          onClose={() => setGdprError(null)}
        />
      )}

      {/* Archive Modal */}
      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onArchive={handleArchive}
        isLoading={archiveMutation.isLoading}
        entityName="Candidate"
        archiveReasons={ARCHIVE_REASONS}
        showDuplicateField={true}
        showGdprWarning={true}
      />
    </div>
  );
};

CandidateStatusControl.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string,
    archive_reason: PropTypes.string,
    duplicate_of_id: PropTypes.number,
    open_for_more_projects: PropTypes.bool,
  }).isRequired,
  onStatusChange: PropTypes.func,
  showTransitions: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  disabled: PropTypes.bool,
};

/**
 * GDPR Consent Required Modal
 * Candidate-specific modal for GDPR consent errors
 */
const GdprConsentModal = ({ error, onClose }) => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }}
  >
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "24px",
        width: "100%",
        maxWidth: "480px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "#fef3c7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#d97706"
            strokeWidth="2"
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: "600",
              color: "#d97706",
            }}
          >
            GDPR Consent Required
          </h3>
        </div>
      </div>

      <p style={{ marginBottom: "16px", color: "#374151", lineHeight: "1.5" }}>
        {error.message}
      </p>

      <div
        style={{
          padding: "12px",
          backgroundColor: "#fef3c7",
          borderRadius: "6px",
          marginBottom: "20px",
          fontSize: "14px",
          color: "#92400e",
        }}
      >
        <strong>Next Steps:</strong>
        <ol style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>
            Go to the <strong>GDPR</strong> tab on this candidate's profile
          </li>
          <li>
            Click <strong>Request Consent</strong> or{" "}
            <strong>Record External Consent</strong>
          </li>
          <li>
            Once consent is obtained, you can change status to "Ready for Sale"
          </li>
        </ol>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
        <button
          onClick={onClose}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            backgroundColor: "white",
            cursor: "pointer",
          }}
        >
          Close
        </button>
        <a
          href={`/candidates/${error.candidateId}?tab=gdpr`}
          style={{
            padding: "8px 16px",
            borderRadius: "6px",
            border: "none",
            backgroundColor: "#1E40AF",
            color: "white",
            textDecoration: "none",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          Go to GDPR Tab
        </a>
      </div>
    </div>
  </div>
);

GdprConsentModal.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    candidateId: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * CandidateStatusBadge
 * Simple badge component for use in lists
 */
export const CandidateStatusBadge = ({
  status,
  size = "small",
  onClick,
  clickable = false,
}) => (
  <StatusBadge
    status={status}
    statusConfig={STATUS_CONFIG}
    defaultStatus="preparation"
    size={size}
    onClick={onClick}
    clickable={clickable}
  />
);

CandidateStatusBadge.propTypes = {
  status: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
};

/**
 * CandidateStatusBadgeWithActions
 *
 * Interactive status badge with popover for quick status changes from list view.
 * ADO Feature #4936: Candidate Status Workflow
 */
export const CandidateStatusBadgeWithActions = ({
  candidate,
  size = "small",
  onStatusChange,
  disabled = false,
}) => {
  const queryClient = useQueryClient();
  const badgeRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [gdprError, setGdprError] = useState(null);

  const { data: transitionsData } = useQuery(
    ["candidateTransitions", candidate?.id],
    () => recruitingAPI.getAllowedTransitions(candidate.id),
    {
      enabled: !!candidate?.id && !disabled,
      staleTime: 30 * 1000,
    },
  );

  const allowedTransitions = transitionsData?.data?.allowed_transitions || [];
  const allowedStatusValues = allowedTransitions.map((t) => t.value);
  const currentStatus = candidate?.status || "preparation";
  const gdprStatus = transitionsData?.data?.gdpr;

  // Build list of all statuses with their availability
  const allStatuses = Object.entries(STATUS_CONFIG).map(
    ([statusKey, config]) => {
      const isAllowed = allowedStatusValues.includes(statusKey);
      const isCurrent = statusKey === currentStatus;

      // Determine disabled reason
      let disabledReason = null;
      if (isCurrent) {
        disabledReason = "Current status";
      } else if (!isAllowed) {
        // Check for GDPR-specific blocking on ready_for_sale
        if (
          statusKey === "ready_for_sale" &&
          gdprStatus &&
          !gdprStatus.canTransitionToReadyForSale
        ) {
          disabledReason = "GDPR consent required";
        } else {
          disabledReason = getDisabledReason(currentStatus, statusKey);
        }
      }

      return {
        value: statusKey,
        label: config.label,
        color: config.color,
        bgColor: config.bgColor,
        isAllowed,
        isCurrent,
        disabledReason,
      };
    },
  );

  const updateStatusMutation = useMutation(
    (statusData) =>
      recruitingAPI.updateCandidateStatus(candidate.id, statusData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["candidate", String(candidate.id)]);
        queryClient.invalidateQueries(["candidates"]);
        queryClient.invalidateQueries(["candidateTransitions", candidate.id]);
        setIsOpen(false);
        if (onStatusChange) {
          onStatusChange(data.data);
        }
      },
    },
  );

  const archiveMutation = useMutation(
    (archiveData) => recruitingAPI.archiveCandidate(candidate.id, archiveData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["candidate", String(candidate.id)]);
        queryClient.invalidateQueries(["candidates"]);
        queryClient.invalidateQueries(["candidateTransitions", candidate.id]);
        setShowArchiveModal(false);
        setIsOpen(false);
        if (onStatusChange) {
          onStatusChange(data.data);
        }
      },
    },
  );

  const handleClick = (event) => {
    event.stopPropagation();
    // Allow opening for any non-terminal status (even if no allowed transitions - show all with reasons)
    const isTerminalStatus = currentStatus === "archived";
    if (!disabled && !isTerminalStatus && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
      setIsOpen(true);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "archived") {
      setShowArchiveModal(true);
      return;
    }

    // Clear any previous GDPR error
    setGdprError(null);

    try {
      await updateStatusMutation.mutateAsync({ status: newStatus });
    } catch (error) {
      console.error("Status change failed:", error);
      // Check if this is a GDPR consent required error
      const errorResponse = error?.response?.data;
      if (errorResponse?.code === "GDPR_CONSENT_REQUIRED") {
        setGdprError({
          message:
            errorResponse.message ||
            "GDPR consent is required before moving to Ready for Sale status.",
          candidateId: candidate.id,
        });
      }
    }
  };

  const handleArchive = async (archiveData) => {
    try {
      await archiveMutation.mutateAsync(archiveData);
    } catch (error) {
      console.error("Archive failed:", error);
    }
  };

  const isLoading = updateStatusMutation.isLoading || archiveMutation.isLoading;
  const isTerminalStatus = currentStatus === "archived";
  const hasTransitions = !disabled && !isTerminalStatus;

  return (
    <>
      <span
        ref={badgeRef}
        className="header-status-badge"
        style={{ display: "inline-block" }}
      >
        <CandidateStatusBadge
          status={candidate?.status}
          size={size}
          onClick={handleClick}
          clickable={hasTransitions}
        />
      </span>

      {/* GDPR Consent Required Modal */}
      {gdprError && (
        <GdprConsentModal
          error={gdprError}
          onClose={() => setGdprError(null)}
        />
      )}

      {/* Popover for status transitions */}
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        >
          <div
            style={{
              position: "fixed",
              top: popoverPosition?.top,
              left: popoverPosition?.left,
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              border: "1px solid #e5e7eb",
              minWidth: "260px",
              zIndex: 1000,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "8px 12px",
                fontSize: "11px",
                color: "#9ca3af",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              Change status to:
            </div>
            {allStatuses.map((status) => {
              const isDisabled =
                !status.isAllowed || status.isCurrent || isLoading;
              return (
                <button
                  key={status.value}
                  onClick={() =>
                    !isDisabled && handleStatusChange(status.value)
                  }
                  disabled={isDisabled}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    width: "100%",
                    padding: "10px 16px",
                    fontSize: "13px",
                    textAlign: "left",
                    border: "none",
                    backgroundColor: status.isCurrent ? "#f3f4f6" : "white",
                    color: isDisabled ? "#9ca3af" : "#374151",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    opacity: isDisabled && !status.isCurrent ? 0.6 : 1,
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled)
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled)
                      e.currentTarget.style.backgroundColor = status.isCurrent
                        ? "#f3f4f6"
                        : "white";
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: isDisabled ? "#d1d5db" : status.color,
                        flexShrink: 0,
                      }}
                    />
                    <span>{status.label}</span>
                    {status.isCurrent && (
                      <span
                        style={{
                          fontSize: "10px",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          backgroundColor: status.bgColor,
                          color: status.color,
                          fontWeight: "500",
                        }}
                      >
                        Current
                      </span>
                    )}
                  </span>
                  {status.disabledReason && !status.isCurrent && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#9ca3af",
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {status.disabledReason}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <ArchiveModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onArchive={handleArchive}
        isLoading={archiveMutation.isLoading}
        entityName="Candidate"
        archiveReasons={ARCHIVE_REASONS}
        showDuplicateField={true}
        showGdprWarning={true}
      />
    </>
  );
};

CandidateStatusBadgeWithActions.propTypes = {
  candidate: PropTypes.shape({
    id: PropTypes.number.isRequired,
    status: PropTypes.string,
  }).isRequired,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  onStatusChange: PropTypes.func,
  disabled: PropTypes.bool,
};

// Re-export configs for backward compatibility
export {
  STATUS_CONFIG,
  ARCHIVE_REASONS,
  STATUS_ORDER,
  VALID_TRANSITIONS,
  getDisabledReason,
};
export default CandidateStatusControl;
